<template>
  <div class="timestamps"
       :class="{ 'small-screen': isSmallScreen() }">
    <form @submit="handleAdd" class="convert" :class="{'focus': newValueFocus}">
      <input type="text"
             autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
             v-model="newValue"
             id="new-value"
             v-focus="newValueFocus"
             @focus="newValueFocus = true"
             @blur="newValueFocus = false"
             class="new-value"
             placeholder="Enter a timestamp, iso string, date, ..."
             @keydown="handleKeyDown"
             autofocus
      />
      <div class="datetime-picker-button">
        <button type="button" class="date-picker" @click="handleDateTimePicker" title="Open a date/time picker">
        </button>
      </div>
      <button type="submit" class="convert" title="Convert the entered date/time">convert</button>
    </form>

    <div class="error">
      {{error}}
    </div>

    <div v-if="showHelp || items.length === 0"
         class="intro"
         :style="{maxWidth: (contentsWidth + 'px')}">
      <div class="intro-contents">
        <p>Hi there! tttimestamps converts timestamps, ISO strings, dates, and more for you. Just enter any date-like input in the text field above and press enter. Entered inputs appear in an editable table displaying various time formats side by side.
        </p>
        <p>
          Example inputs:
        </p>
        <div style="display: inline-block">
          <ul>
            <li @click="insertExample">1291141800000</li>
            <li @click="insertExample">2017-01-14T20:09:50.912Z</li>
            <li @click="insertExample">08/25/2016</li>
          </ul>
        </div>
        <div style="display: inline-block">
          <ul>
            <li @click="insertExample">2014-11-30 18:30</li>
            <li @click="insertExample">2014-10-06 22:45:00 UTC</li>
            <li @click="insertExample">today</li>
          </ul>
        </div>
        <div style="display: inline-block">
          <ul>
            <li @click="insertExample">tomorrow 08:00</li>
            <li @click="insertExample">last Wednesday</li>
            <li @click="insertExample">yesterday 09:30 PM New York</li>
          </ul>
        </div>
        <p>
          Tip: check out the configuration page <button type="button"
                  class="actions config"
                  title="Configure tttimestamps"
                  @click="handleShowConfig" ></button> to customize table columns and timezones.
        </p>
        <!-- TODO: describe all available types of inputs behind a button "read more" -->
      </div>
      <div class="actions">
        <input v-if="items.length !== 0"
               class="dismiss"
               type="button"
               value="Got it!"
               @click="hideIntro" />
      </div>
    </div>

    <div class="table">
      <div class="row head" :style="{visibility: (items.length > 0) ? 'visible' : 'hidden'}">
        <div class="col drag-handle-header"></div>{{
        }}<div v-if="descriptions"
             class="col head"
             :style="{ width: DESCRIPTIONS_WIDTH + 'px'}">
          <div class="label">
            description
          </div>
        </div>{{
        }}<div v-for="column in columns"
             class="col head"
             :style="{ width: column.definition.width + 'px' }">
          <div class="label">
            <div v-for="line in label(column).split('\n')" class="label-line">{{line}}</div>
          </div>
        </div>{{
        }}<div class="col action-header"></div>{{
        }}<div class="col action-header"></div>
      </div>

      <div id="drag-container" v-dragula="items" service="itemsService" drake="items">
        <div v-for="(item, index) in limitedItems"
             :key="item.id"
             class="row"
             :class="{'focus': !!item.focus }"
             @keydown="event => handleTableKeyDown(event, index)" >
          <div class="col drag-handle" title="Drag this row up or down to reorder (Ctrl + Arrow up/down)">
            <div class="drag-icon"></div>
          </div>{{
          }}<div v-if="descriptions"
               class="col"
               :style="{ width: DESCRIPTIONS_WIDTH + 'px'}">
            <div class="label cell">description</div>
            <input type="text"
                   autocomplete="off" autocorrect="off" autocapitalize="off"
                   :value="item.timestamp.description"
                   @input="event => update(index, item.timestamp.value, item.timestamp.type, item.timestamp.timezone, item.timestamp.period, event.target.value)"
                   @focus="event => focus(index, 'description')"
                   @blur="event => blur(index, 'description')" />
          </div>{{
          }}<div v-for="column in columns"
                       class="col"
                       :style="{ width: column.definition.width + 'px' }" >
            <div class="label cell">{{label(column)}}</div>
            <input type="text"
                   autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
                   :value="item.timestamp.format(column.type, column.timezone, column.period)"
                   @input="event => update(index, event.target.value, column.type, column.timezone, column.period, item.timestamp.description)"
                   @focus="event => focus(index, column)"
                   @blur="event => blur(index, column)"
                   :class="{ invalid: item.timestamp.type === column.type && !item.timestamp.isValid }"
                   :readonly="column.definition.isReadonly"
                   :placeholder="placeholder(column)" />

            <div v-if="column.type === 'dateTime' && day(item.timestamp, column.timezone)"
                 class="day-tag"
                 :class="{'focus': item.focus === column}"
                 :title="fullDay(item.timestamp, column.timezone)">
              {{day(item.timestamp, column.timezone)}}
            </div>
          </div>{{
          }}<div class="col action duplicate" title="Click to duplicate this row (Ctrl + D)">
            <input type="button"
                   class="action duplicate"
                   value=""
                   @click="duplicate(index)"
                   @focus="event => focus(index, 'duplicate')"
                   @blur="event => blur(index, 'duplicate')" />
          </div>{{
          }}<div class="col action remove" title="Click to remove this row (Ctrl + Del)">
              <input type="button"
                     class="action remove"
                     value=""
                     @click="remove(index)"
                     @focus="event => focus(index, 'remove')"
                     @blur="event => blur(index, 'remove')" />
          </div>
        </div>
      </div>
    </div>

    <div>
      <a href="#"
         v-if="manyItems && limited"
         @click="toggleLimit"
         :title="`show all ${items.length} items`"
         class="table-action" >
        show all ({{items.length}})
      </a>
      <a href="#"
         v-if="manyItems && !limited"
         @click="toggleLimit"
         :title="`show only the first ${ROW_LIMIT} items`"
         class="table-action" >
        show only the first {{ROW_LIMIT}} items
      </a>
      <a href="#"
         v-if="items.length > 0"
         @click="removeAll"
         class="table-action">
        clear all
      </a>
    </div>
  </div>
</template>

<script>
import 'dragula/dist/dragula.css'
import './material-datetime-picker.scss'
import './TimeTable.scss'

import Vue from 'vue'
import { focus } from 'vue-focus'
import { Vue2Dragula } from 'vue2-dragula'
import moment from 'moment-timezone'
import shortid from 'shortid'
import MaterialDateTimePicker from 'material-datetime-picker'
import { tttime } from '../tttime/tttime'
import { autoComplete } from '../tttime/autoComplete'
import { parseTTTime } from '../tttime/parseTTTime'
import { charFromKeyCode } from '../utils/charFromKeyCode'

const ROW_LIMIT = 10        // max number of displayed timestamps in table
const MAX_TIMESTAMPS = 100  // max number timestamps
const DESCRIPTIONS_WIDTH = 120 // pixels
const CELL_SIZE = 30   // pixels

Vue.use(Vue2Dragula)

// singleton datePicker
let datePicker = null

function saveTimestamps (timestamps) {
  window.localStorage['timestamps'] = JSON.stringify(timestamps)
}

function loadTimestamps () {
  const stringifiedTimestamps = window.localStorage['timestamps']

  if (stringifiedTimestamps) {
    try {
      return JSON.parse(stringifiedTimestamps).map(item => {
        return tttime(item.value, item.type, item.timezone, item.period, item.description)
      })
    } catch (err) {
      console.error('Failed to parse timestamps from localStorage', stringifiedTimestamps)
    }
  }

  return [
    parseTTTime('tomorrow'),
    parseTTTime('now'),
    parseTTTime('yesterday')
  ]
}

export default {
  name: 'timetable',

  directives: { focus },

  props: ['descriptions', 'columns', 'showHelp', 'showConfig'],

  data () {
    const timestamps = loadTimestamps()
    const items = timestamps.map(timestamp => ({
      id: shortid.generate(),
      timestamp
    }))

    return {
      items,
      newValueFocus: true,
      newValue: '',
      error: '',
      limited: true // if limited, only the first 10 items are displayed
    }
  },

  computed: {
    ROW_LIMIT: function () {
      return ROW_LIMIT
    },

    limitedItems: function () {
      return this.limited
          ? this.items.slice(0, ROW_LIMIT)
          : this.items
    },

    manyItems: function () {
      return this.items.length > ROW_LIMIT
    },

    extendedWidth: function () {
      const margins = 3

      return this.contentsWidth +
          (3 * CELL_SIZE + margins) +  // the drag column and actions column
          (2 * CELL_SIZE + margins)    // extra whitespace
    },

    contentsWidth: function () {
      let width = 0
      const margins = 3

      if (this.descriptions !== false) {
        width += DESCRIPTIONS_WIDTH
      }

      this.columns.forEach(column => {
        width += (column.definition.width + margins)
      })

      return width
    },

    DESCRIPTIONS_WIDTH: () => DESCRIPTIONS_WIDTH
  },

  methods: {
    day: function (value, timezone) {
      return value.isValid && timezone ? value.toMoment().tz(timezone).format('ddd') : ''
    },

    fullDay: function (value, timezone) {
      return value.isValid && timezone ? value.toMoment().tz(timezone).format('dddd') : ''
    },

    label: function (column) {
      return column.definition.label(column.timezone, column.period)
    },

    placeholder: function (column) {
      // we don't pass timezone
      return column.definition.label('', column.period).replace(/\n/g, ' ')
    },

    handleAdd: function (event) {
      event.preventDefault()

      try {
        const newTimeObject = this.add(this.newValue)
        if (newTimeObject.isValid) {
          this.newValue = ''
        } else {
          this.error = 'Sorry, I don\'t know how to turn that into a date or time'
        }
      } catch (err) {
        this.error = err.toString()
      }

      // For IE, force the input field to keep focus
      document.getElementById('new-value').focus()
    },

    handleKeyDown: function (event) {
      if (!event.ctrlKey && !event.altKey && !event.metaKey) {
        const input = event.target
        const len = input.value.length
        const start = Math.min(input.selectionStart, input.selectionEnd)
        const end = Math.max(input.selectionStart, input.selectionEnd)

        const c = charFromKeyCode(event)
        if (c !== '' && end === len) {
          // a character is added at the end of the input
          const value = this.newValue.substring(0, start) + c + this.newValue.substring(end)
          const suffix = autoComplete(value)

          if (suffix.length > 0) {
            // we have an auto completion match. Let's take over: insert the new
            // character and the auto completion text, and adjust the caret selection
            event.stopPropagation()
            event.preventDefault()

            this.newValue = value + suffix
            event.target.value = value + suffix
            event.target.setSelectionRange(value.length, value.length + suffix.length)
          }
        }
      }
    },

    // TODO: move the dateTime picker into a separate component

    handleDateTimePicker: function (event) {
      // create once
      if (!datePicker) {
        datePicker = new MaterialDateTimePicker({ 'default': moment().startOf('day') })
          .on('submit', value => {
            const date = value.format('YYYY-MM-DD')
            const time = value.format('HH:mm:ss')
            if (time === '00:00:00') {
              this.add(`${date}`)
            } else {
              this.add(`${date} ${time}`)
            }
          })
          .on('close', () => this.handleCloseDateTimePicker())
      }

      this.openDateTimePicker()
    },

    openDateTimePicker: function () {
      // hide vertical scroll-bar
      document.querySelector('body').className = 'no-scroll'

      // show the date picker
      datePicker.open()

      // update time. Workaround to force the date picker to display it's actual time
      const lastTime = datePicker.get()
      datePicker.set(moment(0))
      datePicker.set(lastTime)
    },

    handleCloseDateTimePicker: function () {
      // show vertical scroll-bar again
      document.querySelector('body').className = ''
    },

    hideIntro () {
      this.$emit('hideHelp')
    },

    handleShowConfig () {
      this.$emit('showConfig')
    },

    insertExample (event) {
      this.newValue = event.target.innerText
      this.newValueFocus = true
    },

    add: function (newValue) {
      const newTimeObject = parseTTTime(newValue)
      if (newTimeObject.isValid) {
        const newItem = {
          id: shortid.generate(),
          timestamp: newTimeObject
        }
        this.items.unshift(newItem)
        this.items.splice(MAX_TIMESTAMPS) // limit if exceeding the max items
      }

      return newTimeObject
    },

    update: function (index, newValue, newType, newTimezone, newPeriod, newLabel) {
      const newItem = {
        id: this.items[index].id,
        focus: this.items[index].focus,
        timestamp: tttime(newValue, newType, newTimezone, newPeriod, newLabel)
      }

      this.items.splice(index, 1, newItem)
    },

    duplicate: function (index) {
      this.items.splice(index + 1, 0, {
        id: shortid.generate(),
        timestamp: this.items[index].timestamp
      })
    },

    remove: function (index) {
      this.items.splice(index, 1)
    },

    moveUp: function (index) {
      if (index >= 1) {
        const [item] = this.items.splice(index, 1)
        this.items.splice(index - 1, 0, item)
      }
    },

    moveDown: function (index) {
      const total = (this.limited ? ROW_LIMIT : this.items.length)
      if (index < (total - 1)) {
        const [nextItem] = this.items.splice(index + 1, 1)
        this.items.splice(index, 0, nextItem)
      }
    },

    focus: function (index, column) {
      this.items[index].focus = column
      this.$forceUpdate()
    },

    blur: function (index, column) {
      // handle blur on next tick (after an optional next focus), to prevent
      // the action buttons from being removed before they can get focus
      const item = this.items[index]
      this.blurTimeout = setTimeout(() => {
        if (item && item.focus === column) {
          item.focus = null
          this.$forceUpdate()
        }
      })
    },

    handleTableKeyDown: function (event, index) {
      if (event.ctrlKey || event.metaKey) {
        if (event.keyCode === 38) { // Ctrl+Arrow Up
          event.preventDefault()
          this.moveUp(index)
          setTimeout(() => {
            event.target.focus()
            clearTimeout(this.blurTimeout)
          })
        }

        if (event.keyCode === 40) { // Ctrl+Arrow Down
          event.preventDefault()
          this.moveDown(index)
          setTimeout(() => {
            event.target.focus()
            clearTimeout(this.blurTimeout)
          })
        }

        if (event.keyCode === 46) { // Ctrl+Del
          event.preventDefault()
          this.remove(index)
          // FIXME: set focus to next line (or if no next line, to previous line)
        }

        if (event.keyCode === 68) { // Ctrl+D
          event.preventDefault()
          this.duplicate(index)
        }
      }

      // TODO: Arrow Up to move focus a line up
      // TODO: Arrow Down to move focus a line down
    },

    focusTo (rowIndex, columnIndex, charIndex) {
      // TODO: implement focusTo
    },

    toggleLimit: function (event) {
      event.preventDefault()

      this.limited = !this.limited
    },

    removeAll: function (event) {
      event.preventDefault()
      this.items = []
    },

    isSmallScreen: function () {
      return window.innerWidth < this.extendedWidth
    },

    resize: function () {
      this.$forceUpdate()
    }
  },

  watch: {
    items: function (items) {
      const timestamps = items.map(item => item.timestamp)

      saveTimestamps(timestamps)
    },
    newValue: function () {
      // clear any error on change of the input text
      if (this.error !== '') {
        this.error = ''
      }
    }
  },

  created () {
    this.$dragula.createService({
      name: 'itemsService',
      drakes: {
        items: {
          moves: function (el, source, handle, sibling) {
            return handle.classList.contains('drag-handle') ||
                handle.classList.contains('drag-handle-contents') ||
                handle.classList.contains('drag-icon')
          }
        }
      }
    })
  },

  mounted: function () {
    window.addEventListener('resize', this.resize)
  },

  beforeDestroy: function () {
    window.removeEventListener('resize', this.resize)
  }
}

</script>



// WEBPACK FOOTER //
// TimeTable.vue?eb09f6c4